import {
  CategorySection,
  LocalOfferLocation,
  OfferSource,
  VendorItem,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import DynamicMapCard from 'features/rewards/components/MapCard/DynamicMapCard';
import OfferListItem from 'features/rewards/components/OfferListItem/OfferListItem';
import {
  CATEGORY_ALL_ID,
  CATEGORY_ALL_LABEL,
  TOP_OFFERS_FOR_YOU_ID,
} from 'features/rewards/constants/constants';
import { mapVendorItemToLocalOfferLocation } from 'features/rewards/helpers/mapVendorItemToLocalOfferLocation';
import shouldShowMapForCategoryName from 'features/rewards/helpers/shouldShowMapForCategoryName.helpers';
import { useUserCoordinates } from 'features/rewards/hooks/useUserCoordinates';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import { FeatureFlagKey } from 'services/statsig/types';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import theme from 'shared/styles/theme';

import { ScrollPersistenceResult } from './hooks/useScrollPersistence';

type SectionHeaderItem = { type: 'sectionHeader'; title: string; id: string };
type VendorListItem = {
  type: 'vendor';
  vendor: VendorItem;
  isLastInSection: boolean;
};
type GapItem = { type: 'gap'; id: string };
type ListItem = SectionHeaderItem | VendorListItem | GapItem;

interface OffersListProps {
  sections?: CategorySection[];
  selectedCategoryName: string;
  onLoadMore?: () => void;
  isLoading?: boolean;
  hidePills?: boolean;
  ref?: React.RefObject<FlatList | null>;
  coords?: { latitude: number; longitude: number } | null;
  selectedCategoryId?: string | null;
  onScroll?: ScrollPersistenceResult['handleScroll'];
  onContentSizeChange?: () => void;
}

const OffersList = ({
  sections = [],
  selectedCategoryName,
  onLoadMore,
  isLoading = false,
  ref,
  coords,
  hidePills,
  selectedCategoryId,
  onScroll,
  onContentSizeChange,
}: OffersListProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'browseByCategory',
  });

  const { isFeatureEnabled } = useStatsig();
  const isDynamicMapEntryPointEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_DYNAMIC_MAP_ENTRY_POINT,
  );

  const coordinates = useUserCoordinates(isDynamicMapEntryPointEnabled);

  const navigateToDetailPage = (id: string): void => {
    router.push({
      pathname: '/rewards/[id]',
      params: { id, sectionName: selectedCategoryName },
    });
  };

  const getOfferParams = (offer: VendorItem) => {
    const params: {
      offerid: string;
      merchantId: string;
      lat?: string;
      lng?: string;
      categoryName?: string;
    } = {
      offerid: offer.id ?? '',
      merchantId: offer.merchantId ?? '',
    };

    if (coords) {
      params.lat = coords.latitude.toString();
      params.lng = coords.longitude.toString();
    }

    if (selectedCategoryName && selectedCategoryName !== CATEGORY_ALL_LABEL) {
      params.categoryName = selectedCategoryName;
    }

    if (
      selectedCategoryName &&
      selectedCategoryName.toLowerCase() === TOP_OFFERS_FOR_YOU_ID
    ) {
      params.categoryName = '';
    }

    return params;
  };

  const navigateToUpsideDetails = (offer: VendorItem): void => {
    router.push({
      pathname: '/rewards/upside-offers/[merchantId]',
      params: getOfferParams(offer),
    });
  };

  const handleActivateOffer = (offer: VendorItem): void => {
    router.push({
      pathname: '/rewards/upside-offers/[merchantId]',
      params: { ...getOfferParams(offer), autoActivate: 'true' },
    });
  };

  const allVendors = sections.flatMap((s) => s.vendors ?? []);

  const localOfferLocations = allVendors
    .map(mapVendorItemToLocalOfferLocation)
    .filter((loc): loc is LocalOfferLocation => loc !== null);

  const firstVendor = allVendors[0];
  const centerLocation = firstVendor?.location
    ? {
        lat: firstVendor.location.latitude,
        lng: firstVendor.location.longitude,
      }
    : null;

  const fallbackLocation = coords
    ? {
        lat: coords.latitude,
        lng: coords.longitude,
      }
    : null;

  const location = centerLocation || fallbackLocation || coordinates;

  const hasVendors = allVendors.length > 0;

  const showMapHeader =
    hasVendors &&
    shouldShowMapForCategoryName(selectedCategoryName) &&
    localOfferLocations &&
    location;

  const populatedSections = sections.filter(
    (section) => (section.vendors?.length ?? 0) > 0,
  );

  const flatData: ListItem[] = populatedSections.flatMap(
    (section, sectionIndex) => {
      const vendors = section.vendors ?? [];
      const items: ListItem[] = [];
      if (sectionIndex > 0) {
        items.push({ type: 'gap', id: `gap-${section.id}` });
      }
      if (section.title) {
        items.push({
          type: 'sectionHeader',
          title: section.title,
          id: section.id,
        });
      }
      vendors.forEach((vendor, index) => {
        items.push({
          type: 'vendor',
          vendor,
          isLastInSection: index === vendors.length - 1,
        });
      });
      return items;
    },
  );

  if (isLoading && allVendors.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingSpinner />
      </View>
    );
  }

  return (
    <FlatList<ListItem>
      ref={ref}
      testID="offers-list"
      data={flatData}
      onScroll={onScroll}
      scrollEventThrottle={16}
      onContentSizeChange={onContentSizeChange}
      ListHeaderComponent={
        showMapHeader ? (
          <>
            {!hidePills ? (
              <SectionDivider labelText={translatedText('nearby')} />
            ) : null}
            <DynamicMapCard
              id={firstVendor?.id ?? ''}
              markerData={localOfferLocations}
              cameraLocation={location}
              onMarkerPress={() => {}}
              onMapCardPress={() => {
                router.push({
                  pathname: '/rewards/nearby-offers/map',
                  params: {
                    category: selectedCategoryId || CATEGORY_ALL_ID,
                    name: selectedCategoryName,
                  },
                });
              }}
              allowMapInteractions={false}
              containerStyle={styles.mapContainer}
            />
            <View style={styles.headerDividerContainer} />
          </>
        ) : null
      }
      onEndReached={onLoadMore ?? undefined}
      onEndReachedThreshold={onLoadMore ? 0.5 : undefined}
      renderItem={({ item }) => {
        if (item.type === 'gap') {
          return <View style={styles.sectionGap} />;
        }
        if (item.type === 'sectionHeader') {
          return <SectionDivider labelText={item.title} />;
        }
        const { vendor, isLastInSection } = item;
        const isUpsideOffer = vendor.offerSource === OfferSource.Upside;
        const shouldShowActivationIcon = isUpsideOffer && !vendor.isActive;

        return (
          <View>
            <OfferListItem
              id={vendor.id}
              titleText={vendor.name}
              subtitleText={vendor.cashbackRateFormatted ?? ''}
              distance={vendor.distanceFormatted ?? undefined}
              address={vendor.address?.addressLine1 ?? undefined}
              iconUrl={vendor.logoUrl ?? undefined}
              onPress={() =>
                isUpsideOffer
                  ? navigateToUpsideDetails(vendor)
                  : navigateToDetailPage(vendor.id)
              }
              analyticsId="browseCategoryOfferSelected"
              customAttributes={{
                offer_id: vendor.id,
                merchant_name: vendor.name,
                category: selectedCategoryName,
              }}
              isDisabled={false}
              activateOfferAction={
                shouldShowActivationIcon
                  ? () => handleActivateOffer(vendor)
                  : undefined
              }
              activateOfferAnalyticsId="offerActivationClicked"
              activateOfferCustomAttributes={{
                offer_id: vendor.id,
                merchant_id: vendor.merchantId,
                merchant_name: vendor.name,
                offer_bracket: selectedCategoryName,
              }}
              tagText={vendor.tag?.text}
              subtitleTextSecondary={vendor.tertiary ?? undefined}
            />
            {!isLastInSection ? (
              <View style={styles.dividerContainer}>
                <HairLineDivider />
              </View>
            ) : null}
          </View>
        );
      }}
      keyExtractor={(item, index) => {
        if (item.type === 'sectionHeader') {
          return `header-${item.id}-${index}`;
        }
        if (item.type === 'gap') {
          return `${item.id}-${index}`;
        }
        return `vendor-${item.vendor.id}-${index}`;
      }}
      contentContainerStyle={styles.listContent}
    />
  );
};

const styles = StyleSheet.create({
  dividerContainer: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  headerDividerContainer: {
    backgroundColor: theme.color.background.secondary,
    height: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
  },
  sectionGap: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
  },
  listContent: {
    flexGrow: 1,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: theme.spacing.xl,
  },
  mapContainer: {
    padding: theme.spacing.m,
  },
});

export default OffersList;
